/* eslint-disable react/require-default-props */
import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import DiscussionPost from 'src/components/thread-detail/DiscussionPost';
import { KatAlert, KatSpinner, KatDivider } from '@amzn/katal-react';
import {
  fetchThread,
  selectThreadDetailPostIds,
  selectHasMorePosts,
  selectThreadDetailPreviousKey,
  selectThreadDetailNextKey,
  selectThreadDetailParentPostPreviousError,
  selectThreadDetailParentPostNextError,
  selectHasPreviousPosts,
  selectIsNextLoading,
  selectIsPreviousLoading,
  selectThreadDetailFetchStatus,
  selectFocusedPostId,
  selectFilter,
  selectSort,
  threadDetailActions,
} from 'src/redux/slices/threadDetailSlice';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { AppState } from 'src/redux/store';
import InfiniteScroller from 'src/components/InfiniteScroller';
import { CursorDirection } from 'src/types/DiscussionPost';
import RepliesContainer from 'src/components/thread-detail/RepliesContainer';
import FetchStatus from 'src/types/FetchStatus';
import { isStatusLoading } from 'src/utils';
import { QuoteRef } from 'src/types/Quotes';

interface PostContainerProps {
  threadId: string
  quoteRef?: QuoteRef;
}

const PostContainer: React.FC<PostContainerProps> = ({ threadId, quoteRef }) => {
  const dispatch = useAppDispatch();
  const { query } = useRouter();
  const { t } = useTranslation();
  const previousCursor = useAppSelector((state: AppState) => selectThreadDetailPreviousKey(state));
  const nextCursor = useAppSelector((state: AppState) => selectThreadDetailNextKey(state));
  const hasMorePosts = useAppSelector((state: AppState) => selectHasMorePosts(state));
  const hasPreviousPosts = useAppSelector((state: AppState) => selectHasPreviousPosts(state));
  const isNextLoading = useAppSelector(selectIsNextLoading);
  const isPreviousLoading = useAppSelector(selectIsPreviousLoading);
  const parentPostIds = useAppSelector(selectThreadDetailPostIds);
  const parentPostNextError = useAppSelector(selectThreadDetailParentPostNextError);
  const parentPostPreviousError = useAppSelector(selectThreadDetailParentPostPreviousError);
  const threadFetchStatus = useAppSelector(selectThreadDetailFetchStatus);
  const focusedPostId = useAppSelector(selectFocusedPostId);
  const postsRefs: Record<string, HTMLElement> = {};
  const sortBy = useAppSelector(selectSort);
  const filterBy = useAppSelector(selectFilter);

  const fetchNext = () => dispatch(fetchThread({
    threadId,
    cursor: nextCursor,
    direction: CursorDirection.next,
    sortBy,
    filterBy,
  }));

  const fetchPrevious = () => dispatch(fetchThread({
    threadId,
    cursor: previousCursor,
    direction: CursorDirection.previous,
    sortBy,
    filterBy,
  }));

  useEffect(() => {
    const focusedPost = focusedPostId || query.postId as string;
    if (threadFetchStatus === FetchStatus.complete && !!focusedPost) {
      if (postsRefs[focusedPost]) {
        postsRefs[focusedPost].scrollIntoView();
        dispatch(threadDetailActions.setFocusedPostId(''));
      }
    }
  }, [threadFetchStatus, focusedPostId]);

  if (isStatusLoading(threadFetchStatus)) {
    return (
      <div data-testid="post-container-spinner" className="h-screen text-center">
        <KatSpinner className="m-4" size="small" />
      </div>
    );
  }

  return (
    <div className="ltr:ml-[6.875rem] rtl:mr-[6.875rem]">
      {
        parentPostPreviousError && (
        <KatAlert
          data-testid="previous-posts-failure-msg"
          persistent
          variant="warning"
          header={t('request_threadDetailsFetchFailureHeader')}
          description={t('request_threadDetailsFetchFailureDescription')}
        />
        )
      }
      <InfiniteScroller
        fetchPrevious={fetchPrevious}
        fetchNext={fetchNext}
        hasMore={hasMorePosts}
        hasPrevious={hasPreviousPosts}
        isNextLoading={isNextLoading}
        isPreviousLoading={isPreviousLoading}
        endMessage={t('threadDetail_allPostsLoaded')}
      >
        {parentPostIds.map((id) => (
          <div
            ref={(element: HTMLDivElement) => {
              postsRefs[id] = element;
            }}
            data-testid="parent-post"
            key={id}
            id={id}
          >
            <DiscussionPost
              postId={id}
              quoteRef={quoteRef}
            />
            <KatDivider variant="athens" className="bg-kat-seattle" />
            <RepliesContainer
              quoteRef={quoteRef}
              postRefs={postsRefs}
              threadId={threadId}
              postId={id}
            />
          </div>
        ))}
      </InfiniteScroller>
      {
        parentPostNextError && (
        <KatAlert
          data-testid="next-posts-failure-msg"
          persistent
          variant="warning"
          header={t('request_threadDetailsFetchFailureHeader')}
          description={t('request_threadDetailsFetchFailureDescription')}
        />
        )
      }
    </div>
  );
};

export default React.memo(PostContainer);
